import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Campaign,
  CampaignType_Enum as CampaignType,
  Status_Enum as CampaignStatus,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { DaysOfWeek } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/bulk.pb';
import { useAppScopeStore } from '@frontend/scope';
import { BulkMessageContext, BulkMessageEditorContext } from './bulk-message-editor.context';
import { isReadOnly } from './components/campaign-editor/utils';
import { EditorView, SaveAs, UnsavedChangesProps } from './types';
import { createUpdateMask } from './utils/create-update-mask';

type Props = PropsWithChildren & {
  campaignType: CampaignType;
  initialCampaign?: Campaign;
  initialLocationIds?: string[];
  saveAs: SaveAs;
  unsavedChangesProps: UnsavedChangesProps;
  view: EditorView;
};

const defaultSpecificSendDays: DaysOfWeek = {
  sunday: false,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
};

const hasAnySelectedSendDay = (days?: DaysOfWeek) => {
  if (!days) return false;
  return Object.values(days).some(Boolean);
};

export const BulkMessageEditorProvider = ({
  campaignType,
  children,
  initialCampaign,
  initialLocationIds,
  saveAs,
  unsavedChangesProps,
  view,
}: Props) => {
  const { selectedLocationIds } = useAppScopeStore();

  // State from the campaign
  const [attachmentMediaIds, setAttachmentMediaIds] = useState<string[] | undefined>(
    initialCampaign?.attachmentMediaIds
  );
  const [audienceCount, setAudienceCount] = useState<number>(initialCampaign?.messageCounts?.total ?? 0);
  const [campaignId, setCampaignId] = useState<string>(initialCampaign?.campaignId || '');
  const [currentStatus, setCurrentStatus] = useState<CampaignStatus>(
    initialCampaign?.currentStatus ?? CampaignStatus.UNSPECIFIED
  );
  const [locationIds, setLocationIds] = useState<string[]>(
    initialCampaign?.locationIds ?? initialLocationIds ?? selectedLocationIds
  );
  const [messageCount, setMessageCount] = useState<number>(initialCampaign?.usage?.total ?? 0);
  const [messageTemplate, setMessageTemplate] = useState<string>(initialCampaign?.messageTemplate ?? '');
  const [sendDays, setSendDays] = useState<DaysOfWeek | undefined>(initialCampaign?.sendDays ?? undefined);
  const [sendPerDay, setSendPerDay] = useState<number>(initialCampaign?.sendPerDay ?? 0);
  const [startSendAt, setStartSendAt] = useState<string>(initialCampaign?.startSendAt ?? '');
  const [subject, setSubject] = useState<string>(initialCampaign?.subject ?? '');
  const [templateId, setTemplateId] = useState<string | undefined>(initialCampaign?.templateId);
  const [templateJson, setTemplateJson] = useState<string>(initialCampaign?.templateJson ?? '');
  const [thumbnailMediaId, setThumbnailMediaId] = useState<string>(initialCampaign?.thumbnailMediaId ?? '');
  const [thumbnailUrl, setThumbnailUrl] = useState<string>(initialCampaign?.thumbnailUrl ?? '');
  const [title, setTitle] = useState<string>(initialCampaign?.title ?? '');

  // Other form state
  const [activeStep, setActiveStep] = useState<number>(1);
  const [consent, setConsent] = useState<boolean>(false);
  const [enableValidation, setEnableValidation] = useState<boolean>(false);

  // derived state
  const readOnly = isReadOnly(initialCampaign);
  const getCampaignData = useCallback((): Campaign => {
    const resolvedSendDays =
      sendPerDay > 0 ? (hasAnySelectedSendDay(sendDays) ? sendDays : defaultSpecificSendDays) : undefined;

    return {
      attachmentMediaIds: attachmentMediaIds?.length ? attachmentMediaIds : undefined,
      campaignId,
      currentStatus,
      locationIds,
      messageTemplate,
      sendDays: resolvedSendDays,
      sendPerDay,
      startSendAt,
      subject,
      // TODO: Need to verify if we need to pass on update or not  -  templateId: currentStatus === CampaignStatus.SCHEDULED ? undefined : templateId,
      templateId,
      templateJson,
      title,
      type: campaignType,
    };
  }, [
    attachmentMediaIds,
    campaignId,
    campaignType,
    currentStatus,
    locationIds,
    messageTemplate,
    sendDays,
    sendPerDay,
    startSendAt,
    subject,
    templateId,
    templateJson,
    title,
  ]);

  // TODO: look into optimizing this logic (https://github.com/weave-lab/frontend/pull/15847#discussion_r2683985945)
  // TODO: check to see if we can leverage the `compareExistingCampaign` function works instead of needing this separate function
  const compareEmpty = useCallback(() => {
    const checkedConditions = [
      !!attachmentMediaIds?.length,
      !!messageTemplate,
      Object.values(sendDays || {}).length > 0,
      sendPerDay > 0,
      !!startSendAt,
      !!subject,
      !!templateJson,
      !!title,
    ];

    return checkedConditions.filter(Boolean).length > 0;
  }, [attachmentMediaIds, messageTemplate, sendDays, sendPerDay, startSendAt, subject, templateJson, title]);

  const compareExistingCampaign = useCallback(() => {
    const updatedCampaign = getCampaignData();
    const { paths } = createUpdateMask(initialCampaign, updatedCampaign);
    // filter out the currentStatus field since when the button is manually clicked, the currentStatus will almost always be different
    // and when it is different, those will not show up in this array
    const filteredPaths = paths.filter((path) => path !== 'campaign.currentStatus');
    return filteredPaths.length > 0;
  }, [getCampaignData, initialCampaign]);

  const { setChangesExist } = unsavedChangesProps;
  useEffect(() => {
    if (!initialCampaign || currentStatus === CampaignStatus.UNSPECIFIED) {
      // compare against empty values
      const hasChanges = compareEmpty();
      setChangesExist(hasChanges);
    } else {
      // compare against the existing campaign
      const hasChanges = compareExistingCampaign();
      setChangesExist(hasChanges);
    }
  }, [compareEmpty, compareExistingCampaign, currentStatus, initialCampaign, setChangesExist]);

  const contextValue: BulkMessageContext = useMemo(
    () => ({
      activeStep,
      attachmentMediaIds,
      audienceCount,
      campaignId,
      campaignType,
      consent,
      currentStatus,
      enableValidation,
      getCampaignData,
      initialCampaign,
      locationIds,
      messageCount,
      messageTemplate,
      readOnly,
      saveAs,
      sendDays,
      sendPerDay,
      setActiveStep,
      setAttachmentMediaIds,
      setAudienceCount,
      setCampaignId,
      setConsent,
      setCurrentStatus,
      setEnableValidation,
      setLocationIds,
      setMessageCount,
      setMessageTemplate,
      setSendDays,
      setSendPerDay,
      setStartSendAt,
      setSubject,
      setTemplateId,
      setTemplateJson,
      setThumbnailMediaId,
      setThumbnailUrl,
      setTitle,
      startSendAt,
      subject,
      templateId,
      templateJson,
      thumbnailMediaId,
      thumbnailUrl,
      title,
      unsavedChangesProps,
      view,
    }),
    [
      activeStep,
      attachmentMediaIds,
      audienceCount,
      campaignId,
      campaignType,
      consent,
      currentStatus,
      enableValidation,
      getCampaignData,
      initialCampaign,
      locationIds,
      messageCount,
      messageTemplate,
      readOnly,
      saveAs,
      sendDays,
      sendPerDay,
      startSendAt,
      subject,
      templateId,
      templateJson,
      thumbnailMediaId,
      thumbnailUrl,
      title,
      unsavedChangesProps,
      view,
    ]
  );

  return <BulkMessageEditorContext.Provider value={contextValue}>{children}</BulkMessageEditorContext.Provider>;
};
